import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { doc, getDoc } from "firebase/firestore"
import { motion } from "framer-motion"
import {
  ArrowLeft,
  Calendar,
  ChevronRight,
  Gauge,
  Loader2,
  Tag,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { ImageCarousel } from "@/components/VehicleView/ImageCarousel"
import { FinancingCalculator } from "@/components/VehicleView/FinancingCalculator"
import { VehicleSpecs } from "@/components/VehicleView/VehicleSpecs"
import { FinancingRequestDialog } from "@/components/forms/FinancingRequestDialog"

import { useGlobalState } from "@/hooks/context/global/useGlobalState"
import { database } from "@/firebase"
import type { Vehicle } from "@/types"

const formatPrice = (price?: number | null) =>
  Number.isFinite(price) ? (price as number).toLocaleString() : "0"

export default function VehiclePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { vehicles, selectedVehicle, setSelectedVehicle } = useGlobalState()

  const [vehicle, setVehicleLocal] = useState<Vehicle | null>(() => {
    if (selectedVehicle?.id === id) return selectedVehicle
    return vehicles.find((v) => v.id === id) || null
  })
  const [loading, setLoading] = useState(!vehicle)
  const [notFound, setNotFound] = useState(false)
  const [showApplicationForm, setShowApplicationForm] = useState(false)

  useEffect(() => {
    let active = true

    const fromStore =
      (selectedVehicle?.id === id ? selectedVehicle : null) ||
      vehicles.find((v) => v.id === id) ||
      null

    if (fromStore) {
      setVehicleLocal(fromStore)
      setSelectedVehicle(fromStore)
      setLoading(false)
      setNotFound(false)
      return
    }

    if (!id) {
      setNotFound(true)
      setLoading(false)
      return
    }

    setLoading(true)
    ;(async () => {
      try {
        const snap = await getDoc(doc(database, "vehicles", id))
        if (!active) return
        if (!snap.exists()) {
          setNotFound(true)
          setVehicleLocal(null)
          return
        }
        const data = { ...(snap.data() as Vehicle), id: snap.id }
        setVehicleLocal(data)
        setSelectedVehicle(data)
      } catch {
        if (active) setNotFound(true)
      } finally {
        if (active) setLoading(false)
      }
    })()

    return () => {
      active = false
    }
  }, [id, vehicles, selectedVehicle, setSelectedVehicle])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior })
  }, [id])

  const handleOpenApplication = () => {
    if (!vehicle) return
    setShowApplicationForm(true)
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4">
        <Loader2 className="h-8 w-8 animate-spin text-[#39367D]" />
        <p className="text-sm text-gray-500">Cargando vehículo…</p>
      </div>
    )
  }

  if (notFound || !vehicle) {
    return (
      <div className="mx-auto flex min-h-[50vh] w-full max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Vehículo no encontrado</h1>
        <p className="text-sm text-gray-600">
          Es posible que haya sido retirado del catálogo o que el enlace esté roto.
        </p>
        <Button onClick={() => navigate("/catalogo")}>Volver al catálogo</Button>
      </div>
    )
  }

  const displayPrice = vehicle.salePrice ?? 0
  const initPrice =
    vehicle.initPrice ?? Math.round(displayPrice * 0.3)

  return (
    <div className="bg-[#F4F5F9]">
      <Helmet>
        <title>{vehicle.name} | Oriental Ramírez</title>
        <meta
          name="description"
          content={
            vehicle.description?.slice(0, 160) ||
            `${vehicle.brand?.name ?? ""} ${vehicle.name} disponible en Oriental Ramírez.`
          }
        />
      </Helmet>

      {/* Breadcrumb / volver */}
      <div className="mx-auto w-full max-w-[1280px] px-4 pt-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2 text-gray-700"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Volver
          </Button>
          <nav className="hidden items-center gap-1 text-xs text-gray-500 sm:flex">
            <Link to="/" className="hover:text-gray-800">Inicio</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/catalogo" className="hover:text-gray-800">Catálogo</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="max-w-[200px] truncate text-gray-800">{vehicle.name}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 pb-28 pt-4 sm:px-6 lg:px-8 lg:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]"
        >
          {/* Columna principal */}
          <div className="space-y-5">
            {/* Galería */}
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="relative aspect-[4/3] w-full sm:aspect-[16/10]">
                <ImageCarousel images={vehicle.images} />
              </div>
            </div>

            {/* Cabecera del vehículo */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="flex flex-wrap items-center gap-2">
                {vehicle.brand?.name && (
                  <Badge variant="default" className="bg-[#1F2240] text-white">
                    {vehicle.brand.name}
                  </Badge>
                )}
                {vehicle.category?.name && (
                  <Badge variant="secondary">
                    <Tag className="mr-1 h-3 w-3" />
                    {vehicle.category.name}
                  </Badge>
                )}
                {vehicle.cc && (
                  <Badge variant="outline">
                    <Gauge className="mr-1 h-3 w-3" />
                    {vehicle.cc} cc
                  </Badge>
                )}
                {vehicle.year && (
                  <Badge variant="outline">
                    <Calendar className="mr-1 h-3 w-3" />
                    {vehicle.year}
                  </Badge>
                )}
              </div>

              <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
                {vehicle.name}
              </h1>

              {vehicle.description && (
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600 sm:text-base">
                  {vehicle.description}
                </p>
              )}

              <div className="mt-5 grid gap-3 rounded-xl bg-[#F8F9FD] p-4 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Precio total
                  </p>
                  <p className="text-2xl font-bold text-[#E10F18]">
                    RD$ {formatPrice(displayPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Inicial desde
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    RD$ {formatPrice(initPrice)}
                  </p>
                </div>
              </div>

              {/* CTA desktop */}
              <Button
                className="mt-5 hidden h-11 w-full bg-[#E10F18] text-white hover:bg-[#C40D17] lg:inline-flex"
                onClick={handleOpenApplication}
              >
                Solicitar Financiamiento
              </Button>
            </div>
          </div>

          {/* Columna lateral: tabs financiamiento/specs */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <Tabs defaultValue="financing" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="financing">Financiamiento</TabsTrigger>
                  <TabsTrigger value="specs">Especificaciones</TabsTrigger>
                </TabsList>
                <TabsContent value="financing" className="pt-4">
                  <FinancingCalculator vehicle={vehicle} />
                </TabsContent>
                <TabsContent value="specs" className="pt-4">
                  <VehicleSpecs vehicle={vehicle} />
                </TabsContent>
              </Tabs>
            </div>
          </aside>
        </motion.div>
      </div>

      {/* CTA sticky móvil */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 p-3 backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-[1280px] items-center gap-3">
          <div className="flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Desde
            </p>
            <p className="text-base font-bold text-[#E10F18]">
              RD$ {formatPrice(initPrice)}
            </p>
          </div>
          <Button
            className="h-11 flex-1 bg-[#E10F18] text-white hover:bg-[#C40D17]"
            onClick={handleOpenApplication}
          >
            Solicitar Financiamiento
          </Button>
        </div>
      </div>

      <FinancingRequestDialog
        open={showApplicationForm}
        onOpenChange={setShowApplicationForm}
        vehicle={vehicle}
      />
    </div>
  )
}
